import React, { useState, useEffect, useRef } from "react"
import Img from "gatsby-image"
import BgImg from "gatsby-background-image"
import styled, { keyframes } from "styled-components"
import { CSSTransition, TransitionGroup } from "react-transition-group"

import Reveal from "components/utils/Reveal"
import SectionText from "components/molecules/SectionText"
import Button from "components/atoms/Button"
import Flex from "components/atoms/Flex"
import Box from "components/atoms/Box"
import StandardPadding from "components/atoms/StandardPadding"
import SectionStyling from "components/atoms/SectionStyling"
import { Text } from "components/atoms/Typography"
import { colors } from "utils/styled-constants"

const enter = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0px);
    opacity: 1;
  }
`

const enterText = keyframes`
  from {
    transform: translateX(30px);
    opacity: 0;
  }
  to {
    transform: translateX(0px);
    opacity: 1;
  }
`

const IndicatorWrapper = styled.button`
  opacity: ${props => (props.active ? 1 : 0.25)};
  transition: opacity 0.1s ease;
  padding: 13px 0;
  margin-left: 20px;
  &:hover {
    opacity: ${props => (props.active ? 1 : 0.5)};
  }
`
const Indicator = styled.div`
  height: 2px;
  width: 21px;
  background-color: ${colors.mediumBlue};
`

const mod = (num, mod) => {
  if (num > 0) {
    return num % mod
  }
  return mod - (-num % mod)
}

const CarouselIndicators = props => {
  let indicators = []
  for (let i = 0; i < props.numItems; i++) {
    indicators.push(
      <IndicatorWrapper
        key={i}
        active={props.active === i}
        onClick={() => props.selectActive(i)}
      >
        <Indicator />
      </IndicatorWrapper>
    )
  }
  return <Flex>{indicators}</Flex>
}

const StyledImage = styled(Img)`
  height: 100%;
  width: 100%;
  animation-name: ${enter};
  animation-duration: 0.7s;
`

const AnimatedDiv = styled.div`
  animation-name: ${enterText};
  animation-duration: 0.7s;
`

const exitDuration = 100

const StyledTransitionGroup = styled(TransitionGroup)`
  position: relative;
  .item-enter {
    opacity: 1;
  }
  .item-enter-active {
    opacity: 1;
  }
  .item-exit {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    opacity: 1;
  }
  .item-exit-active {
    position: absolute;
    opacity: 0;
    transition: all ${exitDuration}ms ease-in;
    transition-propery: opacity, transform;
  }
`

const breakpoint = "1024px"

const StyledBgImg = styled(BgImg)`
  flex: 0 0 100%;
  @media (min-width: ${breakpoint}) {
    flex: 0 0 45%;
  }
`

const CarouselItem = props => {
  const { title, link, image } = props
  return (
    <Flex width="100%" flexDirection={{ _: "column", lg: "row" }}>
      {/* <Box flexBasis="50%">
        <Img fluid={image.fluid} />
      </Box> */}
      <Box flex={{ _: "0 0 100%", lg: "0 0 45%" }}>
        <BgImg {...image}>
          <Box
            width="100%"
            pb={{ _: "60%", sm: "50%", lg: "75%", xl: "65%" }}
            height="0px"
          />
        </BgImg>
      </Box>
      {/* <Flex flexDirection="column" justifyContent="stretch" flexBasis="50%">
        <StyledImage fluid={image.fluid} />
      </Flex> */}
      <Box p={{ _: "20px 0px 0px", lg: "40px 40px 40px 50px" }} flexBasis="50%">
        <AnimatedDiv>
          <Text
            minHeight={{
              _: "180px",
              xs: "144px",
              sm: "108px",
              lg: "200px",
              lg: "276px",
              xl: "230px",
            }}
            as="h2"
            variant={{ _: "h2-carousel-mobile", lg: "h2-carousel" }}
          >
            {title}
          </Text>
        </AnimatedDiv>
        <Button
          mt={{ _: "18px", lg: "60px" }}
          variant="gray"
          text="Read the article"
          link={{ externalLink: link }}
          target="_blank"
          rel="noreferrer noopener"
        />
      </Box>
    </Flex>
  )
}

const NewsCarousel = ({ sectionStyling, sectionText, articles, idx }) => {
  const [active, setActive] = useState(0)
  const interval = useRef()

  const selectActive = i => {
    //handleResize()
    setActive(i)
  }

  // useEffect(() => {
  //   //handleResize()
  //   const next = mod(active + 1, articles.length)
  //   interval.current = setInterval(() => setActive(next), 6000)
  //   return () => clearInterval(interval.current)
  // }, [active, articles.length])

  useEffect(() => {
    if (articles.length > 1) {
      let timeout = setTimeout(() => {
        setActive((active + 1) % articles.length)
      }, 6000)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [articles, active])

  return (
    <SectionStyling first={idx === 0} {...sectionStyling}>
      <StandardPadding>
        {sectionText && (
          <Reveal>
            <SectionText {...sectionText} mb={6} maxWidth={42} />
          </Reveal>
        )}
        <StyledTransitionGroup>
          {articles &&
            articles.map((article, i) => {
              if (i === active) {
                return (
                  <CSSTransition
                    timeout={{
                      appear: 10,
                      enter: 10,
                      exit: exitDuration,
                    }}
                    classNames="item"
                    key={i}
                  >
                    <CarouselItem {...article} />
                  </CSSTransition>
                )
              }
              return null
            })}
        </StyledTransitionGroup>
        {articles.length > 1 && (
          <Box pt={{ _: 5, lg: 0 }}>
            <CarouselIndicators
              numItems={articles.length}
              active={active}
              selectActive={i => selectActive(i)}
            />
          </Box>
        )}
      </StandardPadding>
    </SectionStyling>
  )
}

export default NewsCarousel

export const query = graphql`
  fragment NewsCarousel on ContentfulNewsCarousel {
    sectionStyling {
      ...SectionStyling
    }
    sectionText {
      ...SectionText
    }
    articles {
      ...Post
    }
  }
`
